import { useState } from "react"

import { CheckIcon } from "@heroicons/react/24/outline"
import { clsx } from "clsx"

import {
  concertAmountRangeText,
  CONCERT_AMOUNT_CURRENT_LABEL,
  CONCERT_AMOUNT_RANGE_LABEL,
  formatConcertAmount,
  hasConfiguredConcertRange,
} from "./concert-amount-copy"
import type {
  ConcertAmountEligibilityView,
  ConcertCreditTier,
  ConcertOfferPickerBundle,
  ConcertOffersView,
  ConcertOfferView,
  ConcertQuoteView,
  ConcertSelectionView,
} from "./concert-kind-bundle"
import {
  OutOfDateNotice,
  REFRESH_PRICING_BUSY_LABEL,
  REFRESH_PRICING_LABEL,
} from "../../../ProposalPresentation/pricing/pricing-out-of-date"
import {
  btnLink,
  btnPrimary,
  btnSecondary,
  monthlyUsd,
  Pill,
  sectionLabelClass,
} from "../financing-chrome"

export const CONCERT_CREDIT_QUALITY_LABEL = `Credit quality`
export const CONCERT_TERMS_LABEL = `Term`
export const CONCERT_OFFERS_LOAD_ERROR_PROMPT = `Unable to load Concert offers`
export const CONCERT_PROVIDER_FAILED_PROMPT = `Concert did not return offers. Try again.`
export const CONCERT_QUALITY_EMPTY_PROMPT = `Concert returned no offers at this credit quality.`
export const CONCERT_OFFER_REQUIRED_PROMPT = `Choose a term to save a Quote. Credit quality alone selects nothing.`
export const CONCERT_PAYMENT_OUT_OF_DATE_LABEL = `Out of date`

/** A/Excellent, B/Very Good, C/Good, always in that order. */
const CREDIT_QUALITIES: ReadonlyArray<{
  tier: ConcertCreditTier
  quality: string
}> = [
  { tier: `A`, quality: `Excellent` },
  { tier: `B`, quality: `Very Good` },
  { tier: `C`, quality: `Good` },
]

/** The default a seller lands on before anything is saved. */
const DEFAULT_CREDIT_TIER: ConcertCreditTier = `A`

/**
 * `available` is absent: nothing needs saying when offers are usable.
 * `amount_ineligible` is absent too — its prompt names the bound and the
 * price, so it is built from the server's eligibility rather than fixed here.
 */
const statusPrompts = (
  copy: ConcertOfferPickerBundle<unknown>[`copy`],
): Partial<Record<ConcertOffersView[`status`], string>> => ({
  empty: copy.empty,
  pricing_required: copy.pricingRequired,
  provider_failed: CONCERT_PROVIDER_FAILED_PROMPT,
  selected_offer_unavailable: copy.selectedOfferUnavailable,
  unavailable: copy.unavailable,
})

/**
 * The configured range beside the price being measured against it, so the
 * seller can see why Concert is or is not available before anything fails.
 */
const AmountRange = ({
  eligibility,
}: {
  eligibility: ConcertAmountEligibilityView
}): JSX.Element => (
  <dl
    data-testid="concert-amount-range"
    className="flex flex-wrap gap-x-6 gap-y-1 rounded-lg border border-bp-border bg-bp-elevated px-3.5 py-3 text-[length:var(--step--2)]"
  >
    <div className="flex gap-2">
      <dt className="text-bp-text-muted">{CONCERT_AMOUNT_RANGE_LABEL}</dt>
      <dd className="text-bp-text tabular-nums">
        {concertAmountRangeText(eligibility)}
      </dd>
    </div>
    <div className="flex gap-2">
      <dt className="text-bp-text-muted">{CONCERT_AMOUNT_CURRENT_LABEL}</dt>
      <dd className="text-bp-text tabular-nums">
        {formatConcertAmount(eligibility.amount)}
      </dd>
    </div>
  </dl>
)

/**
 * The live row for a saved selection. Concert can replace its calculation
 * model between fetches, so the same product, credit tier, term, and APR
 * counts as the same offer under a new id — the server matches it the same way.
 */
export const matchConcertOffer = (
  offers: readonly ConcertOfferView[],
  selected: ConcertSelectionView | null,
): ConcertOfferView | null => {
  if (selected == null) {
    return null
  }
  return (
    offers.find((offer) => offer.offerId === selected.offerId) ??
    offers.find(
      (offer) =>
        offer.productId === selected.productId &&
        offer.creditTier === selected.creditTier &&
        offer.termYears === selected.termYears &&
        offer.apr === selected.apr,
    ) ??
    null
  )
}

const offerTerms = (offer: { termYears: number; apr: number }): string =>
  `${offer.termYears} years · ${offer.apr.toFixed(2)}% APR`

/**
 * The Concert half of the Loan Product drawer, for any proposal kind: credit
 * quality, the terms Concert returned for it, and an explicit save. Every
 * payment is Concert's own figure, read back from the server — none is
 * calculated here. The bundle binds the reads, the save and the copy.
 */
export const ConcertOfferPicker = <TSaved,>({
  bundle,
  proposalId,
  quote,
  onClose,
}: {
  bundle: ConcertOfferPickerBundle<TSaved>
  proposalId: string
  quote: ConcertQuoteView | null
  onClose: () => void
}): JSX.Element => {
  const { copy } = bundle
  const offersQuery = bundle.useOffers(proposalId)
  const saveOffer = bundle.useSave()
  const refreshPricing = bundle.useRefreshPricing()
  // The seller's pick lives here only; reopening the drawer falls back to what
  // the server saved. Credit quality travels with it so changing quality never
  // carries another quality's row along.
  const [draft, setDraft] = useState<{
    tier: ConcertCreditTier
    offerId: string | null
  } | null>(null)
  const [saved, setSaved] = useState<`quoted` | `saved_only` | null>(null)

  const offers = offersQuery.data ?? null
  const status = offers?.status ?? null
  // Offers and their pricing context describe one price. A refetch is in
  // flight against a price that has already moved, so every payment on
  // screen is provisional until it lands — no selecting or saving through it.
  const settling = offersQuery.isFetching
  const rows =
    status === `available` || status === `selected_offer_unavailable`
      ? (offers?.offers ?? [])
      : []
  const savedSelection = offers?.selectedOffer ?? null
  const savedRow = matchConcertOffer(rows, savedSelection)
  const busy = saveOffer.isLoading || refreshPricing.isLoading || settling
  const canEdit = offers?.canSelect ?? false
  const canRefresh = offers?.canRefreshPricing ?? false

  const tier = draft?.tier ?? savedSelection?.creditTier ?? DEFAULT_CREDIT_TIER
  // The saved row stands selected until the seller picks another, and only
  // while Concert still returns it — a disappeared offer selects nothing.
  // Nothing is ever selected without a save behind it or a click.
  const selectedOfferId = draft?.offerId ?? savedRow?.offerId ?? null
  const qualityRows = rows
    .filter((offer) => offer.creditTier === tier)
    .sort((left, right) => left.termYears - right.termYears)
  // Concert can return two products on the same tier, term, and APR. Name
  // them only when the terms alone would not tell them apart.
  const nameRows = new Set(qualityRows.map((offer) => offer.name)).size > 1
  const chosen =
    qualityRows.find((offer) => offer.offerId === selectedOfferId) ?? null
  // A save is fenced against the price the offers were fetched for, so
  // without that context there is nothing to submit.
  const submittable = chosen != null && offers?.pricingContext != null
  const showSaveAction = canEdit && rows.length > 0

  const amountEligibility = offers?.amountEligibility ?? null
  // A status this bundle does not model still owes the seller a reason.
  const statusPrompt =
    offers == null || status === `available`
      ? null
      : status === `amount_ineligible`
        ? (bundle.amountBlockedPrompt(amountEligibility) ??
          copy.amountIneligibleFallback)
        : (statusPrompts(copy)[offers.status] ?? CONCERT_PROVIDER_FAILED_PROMPT)
  // Retrying the same read cannot change an amount decision — Refresh Pricing
  // after editing the proposal is the way out, and it stays offered below.
  const canRetryOffers =
    statusPrompt != null &&
    status !== `amount_ineligible` &&
    status !== `pricing_required` &&
    status !== `unavailable` &&
    status !== `selected_offer_unavailable`
  // The Quote and the saved selection are separate reads. A save updates the
  // Quote first, so while they disagree the pair would describe the offer the
  // seller just replaced — say nothing rather than something wrong.
  const savedQuote =
    quote != null &&
    savedSelection != null &&
    quote.loanProduct?.id === savedSelection.offerId
      ? quote
      : null
  const showSavedSelection =
    savedSelection != null && (savedQuote != null || quote?.loanProduct == null)

  const submit = (): void => {
    const pricingContext = offers?.pricingContext
    if (!canEdit || busy || chosen == null || pricingContext == null) {
      return
    }
    saveOffer.mutate(
      { proposalId, body: { offerId: chosen.offerId, pricingContext } },
      {
        onSuccess: (result) => {
          // The server's saved selection takes over from the local pick.
          setDraft(null)
          // A 200 without a current payment — a newer write landed first —
          // must not be announced as one.
          setSaved(bundle.savedOutcome(result))
        },
      },
    )
  }

  return (
    <>
      <div className="flex flex-col gap-4">
        <h3 className={sectionLabelClass}>Loan Product</h3>

        {offersQuery.isLoading ? (
          <p className="text-[length:var(--step--1)] text-bp-text-muted">
            Loading Concert offers
          </p>
        ) : null}

        {offersQuery.isError ? (
          <div className="flex flex-col gap-2">
            <p
              role="alert"
              className="text-[length:var(--step--2)] text-bp-danger"
            >
              {CONCERT_OFFERS_LOAD_ERROR_PROMPT}
            </p>
            <button
              type="button"
              onClick={() => {
                void offersQuery.refetch()
              }}
              className={btnLink}
            >
              Retry
            </button>
          </div>
        ) : null}

        {/* Only once a range exists. With no bound configured there is nothing
            to measure against, and a row reading "No limit" is noise on every
            Concert proposal. */}
        {hasConfiguredConcertRange(amountEligibility) && amountEligibility ? (
          <AmountRange eligibility={amountEligibility} />
        ) : null}

        {showSavedSelection && savedSelection ? (
          <SavedSelection
            selection={savedSelection}
            quote={savedQuote}
            unavailable={status === `selected_offer_unavailable`}
          />
        ) : null}

        {statusPrompt ? (
          <div className="flex flex-col items-start gap-2">
            <p
              role="status"
              className={clsx(
                `text-[length:var(--step--2)]`,
                status === `provider_failed`
                  ? `text-bp-danger`
                  : status === `amount_ineligible`
                    ? `text-bp-gold`
                    : `text-bp-text-muted`,
              )}
            >
              {statusPrompt}
            </p>
            {canRetryOffers ? (
              <button
                type="button"
                onClick={() => {
                  void offersQuery.refetch()
                }}
                className={btnLink}
              >
                Try again
              </button>
            ) : null}
          </div>
        ) : null}

        {rows.length > 0 ? (
          <>
            <fieldset className="flex flex-col gap-2">
              <legend className="mb-2 text-[length:var(--step--2)] font-semibold tracking-[0.12em] text-bp-text-muted uppercase">
                {CONCERT_CREDIT_QUALITY_LABEL}
              </legend>
              <div className="flex flex-wrap gap-2">
                {CREDIT_QUALITIES.map((credit) => (
                  <label
                    key={credit.tier}
                    className={clsx(
                      `cursor-pointer rounded-md border px-3 py-1.5 text-[length:var(--step--2)] font-medium transition-colors`,
                      credit.tier === tier
                        ? `border-bp-accent bg-bp-accent/10 text-bp-text`
                        : `border-bp-border-strong text-bp-text-muted hover:border-bp-accent`,
                      busy && `cursor-not-allowed opacity-60`,
                    )}
                  >
                    <input
                      type="radio"
                      name="concert-credit-quality"
                      className="sr-only"
                      value={credit.tier}
                      checked={credit.tier === tier}
                      // A filter over what Concert returned, not a write:
                      // read-only viewers browse it too.
                      disabled={busy}
                      onChange={() => {
                        saveOffer.reset()
                        setSaved(null)
                        // Quality alone never selects a product.
                        setDraft({ tier: credit.tier, offerId: null })
                      }}
                    />
                    {credit.quality}
                  </label>
                ))}
              </div>
            </fieldset>

            <fieldset className="flex flex-col gap-2">
              <legend className="mb-2 text-[length:var(--step--2)] font-semibold tracking-[0.12em] text-bp-text-muted uppercase">
                {CONCERT_TERMS_LABEL}
              </legend>
              {qualityRows.length === 0 ? (
                <p
                  role="status"
                  className="text-[length:var(--step--2)] text-bp-text-muted"
                >
                  {CONCERT_QUALITY_EMPTY_PROMPT}
                </p>
              ) : (
                <div className="flex flex-col gap-2">
                  {qualityRows.map((offer) => (
                    <OfferRow
                      key={offer.offerId}
                      offer={offer}
                      showName={nameRows}
                      checked={offer.offerId === chosen?.offerId}
                      unsaved={
                        offer.offerId === chosen?.offerId &&
                        offer.offerId !== savedRow?.offerId
                      }
                      disabled={!canEdit || busy}
                      onSelect={() => {
                        saveOffer.reset()
                        setSaved(null)
                        setDraft({ tier, offerId: offer.offerId })
                      }}
                    />
                  ))}
                </div>
              )}
            </fieldset>

            {!canEdit ? (
              <p
                role="status"
                className="text-[length:var(--step--2)] text-bp-text-muted"
              >
                {copy.readOnly}
              </p>
            ) : null}

            {canEdit && chosen == null && qualityRows.length > 0 ? (
              <p
                role="status"
                className="text-[length:var(--step--2)] text-bp-text-muted"
              >
                {CONCERT_OFFER_REQUIRED_PROMPT}
              </p>
            ) : null}
          </>
        ) : null}
      </div>

      <div className="mt-auto flex shrink-0 flex-col gap-3 border-t border-bp-border pt-5">
        {saveOffer.isError ? (
          <div
            role="alert"
            className="flex items-center justify-between gap-3 rounded-md border border-bp-danger/40 bg-bp-danger/10 px-3 py-2"
          >
            <span className="text-[length:var(--step--2)] text-bp-danger">
              {/* The server's amount message names the bound and the price;
                  generic save copy would throw that away. */}
              {bundle.describeSaveError(saveOffer.error)}
            </span>
            <button
              type="button"
              disabled={!submittable || busy}
              onClick={submit}
              className="shrink-0 rounded-md border border-bp-danger/50 px-3 py-1 text-[length:var(--step--2)] font-medium text-bp-text disabled:cursor-not-allowed disabled:opacity-50"
            >
              Retry
            </button>
          </div>
        ) : null}
        {refreshPricing.isError ? (
          <p
            role="alert"
            className="text-[length:var(--step--2)] text-bp-danger"
          >
            {bundle.describeRefreshError(refreshPricing.error)}
          </p>
        ) : null}
        {saved ? (
          <p
            role="status"
            className={clsx(
              `flex items-center gap-1.5 text-[length:var(--step--2)] font-medium`,
              saved === `quoted` ? `text-bp-success` : `text-bp-text-muted`,
            )}
          >
            <CheckIcon aria-hidden="true" className="size-4 shrink-0" />
            {saved === `quoted`
              ? copy.savedLabel
              : copy.savedWithoutPaymentLabel}
          </p>
        ) : null}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <button type="button" onClick={onClose} className={btnSecondary}>
            Close
          </button>
          {showSaveAction ? (
            <button
              type="button"
              disabled={!submittable || busy}
              onClick={submit}
              className={btnPrimary}
            >
              {saveOffer.isLoading ? copy.saveBusyLabel : copy.saveLabel}
            </button>
          ) : canRefresh ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                saveOffer.reset()
                setSaved(null)
                refreshPricing.mutate({ proposalId })
              }}
              className={btnPrimary}
            >
              {refreshPricing.isLoading
                ? REFRESH_PRICING_BUSY_LABEL
                : REFRESH_PRICING_LABEL}
            </button>
          ) : null}
        </div>
      </div>
    </>
  )
}

/**
 * One returned term. Concert's ACH payment is printed to the cent exactly as
 * it arrived; nothing here amortizes or rounds it.
 */
const OfferRow = ({
  offer,
  showName,
  checked,
  unsaved,
  disabled,
  onSelect,
}: {
  offer: ConcertOfferView
  showName: boolean
  checked: boolean
  unsaved: boolean
  disabled: boolean
  onSelect: () => void
}): JSX.Element => (
  <label
    data-testid="concert-offer-row"
    className={clsx(
      `flex cursor-pointer items-center justify-between gap-3 rounded-lg border px-3.5 py-3 transition-colors`,
      checked
        ? `border-bp-accent bg-bp-accent/10`
        : `border-bp-border bg-bp-elevated hover:border-bp-accent`,
      disabled && `cursor-not-allowed opacity-60`,
    )}
  >
    <span className="flex items-center gap-3">
      <input
        type="radio"
        name="concert-offer"
        className="size-4 accent-bp-accent"
        value={offer.offerId}
        checked={checked}
        disabled={disabled}
        onChange={onSelect}
      />
      <span className="flex min-w-0 flex-col">
        <span className="text-[length:var(--step--1)] text-bp-text">
          {offerTerms(offer)}
        </span>
        {showName ? (
          <span className="text-[length:var(--step--2)] text-bp-text-muted">
            {offer.name}
          </span>
        ) : null}
      </span>
    </span>
    <span className="flex shrink-0 items-center gap-2">
      {unsaved ? <Pill tone="warn">Unsaved</Pill> : null}
      <span className="text-[length:var(--step--1)] font-semibold text-bp-text tabular-nums">
        {`${monthlyUsd.format(offer.monthlyPayment)}/mo`}
      </span>
    </span>
  </label>
)

/**
 * The saved offer and the Quote it produced. A retained Quote keeps its
 * payment on screen, marked out of date, so the seller can still explain it.
 */
const SavedSelection = ({
  selection,
  quote,
  unavailable,
}: {
  selection: ConcertSelectionView
  quote: ConcertQuoteView | null
  unavailable: boolean
}): JSX.Element => (
  <div
    data-testid="concert-saved-offer"
    className={clsx(
      `flex flex-wrap items-center justify-between gap-3 rounded-lg border px-3.5 py-3`,
      unavailable
        ? `border-bp-danger/40 bg-bp-danger/5`
        : `border-bp-border bg-bp-elevated`,
    )}
  >
    <span className="flex min-w-0 flex-col gap-0.5">
      <span className="text-[length:var(--step--1)] font-semibold text-bp-text">
        {selection.creditQuality} · {offerTerms(selection)}
      </span>
      {quote?.estimatedMonthlyPayment != null ? (
        <span className="flex flex-wrap items-center gap-2 text-[length:var(--step--2)] text-bp-text-muted">
          <span
            data-testid="concert-saved-payment"
            className="text-bp-text tabular-nums"
          >
            {`${monthlyUsd.format(quote.estimatedMonthlyPayment)}/mo`}
          </span>
          {quote.status === `stale` ? (
            <OutOfDateNotice label={CONCERT_PAYMENT_OUT_OF_DATE_LABEL} />
          ) : null}
        </span>
      ) : null}
    </span>
    <span className="flex shrink-0 items-center gap-2">
      <Pill tone="muted">Saved</Pill>
      {unavailable ? <Pill tone="danger">Unavailable</Pill> : null}
    </span>
  </div>
)
